/**
 * Gating atoms.
 *
 * Owns the per-project gating mode, available discriminators (phases or views)
 * for the current study, the active discriminator (which one the main viewport
 * is showing), and flicker state.
 */
import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import type { Discriminator, GatingMode, Phase } from "../types/gating";

/**
 * Active viewport gating mode (the SSoT every layout component reads). Seeded
 * per case by `LabelingPage` from the case's assigned categories
 * (`defaultGatingMode(availableGatingModes(...))`, B2) and only otherwise
 * written by the manual view-mode selector (B3). Never auto-changes within a
 * case. Defaults to "none" so legacy/non-segmentation surfaces keep working.
 */
export const projectGatingModeAtom = atom<GatingMode>("none");

/**
 * Distinct non-"none" gating modes available for the current case, in
 * precedence order. Set per case by `LabelingPage` alongside
 * `projectGatingModeAtom`. The view-mode selector renders only when this has
 * more than one entry (a hybrid project); a single entry means the mode is
 * fixed and there is nothing to switch.
 */
export const availableGatingModesAtom = atom<GatingMode[]>([]);

/**
 * Discriminators available in the current study, in display order. Populated
 * by the layout container when the study is loaded.
 */
export const availableDiscriminatorsAtom = atom<Discriminator[]>([]);

/**
 * Active discriminator — which phase / view is in the main viewport. Null
 * means nothing has been selected yet (the layout should pick a default).
 */
export const activeDiscriminatorAtom = atom<Discriminator | null>(null);

/**
 * Whether the user is currently holding the flicker hotkey. Transient — never
 * persisted. While true, the layout should rapidly cycle the main viewport
 * through `availableDiscriminatorsAtom` at `flickerHzAtom` rate; on release it
 * snaps back to `activeDiscriminatorAtom`.
 */
export const flickerActiveAtom = atom<boolean>(false);

/**
 * Flicker rate in Hz. Persisted to localStorage as a user preference.
 * Default 6 Hz mirrors how radiologists rapid-toggle phases.
 */
export const flickerHzAtom = atomWithStorage<number>("gatingFlickerHz", 6);

/**
 * Helper: returns the available discriminator that matches a given phase, or
 * null if that phase isn't present in this study.
 */
export const findPhaseDiscriminatorAtom = atom((get) => (phase: Phase) => {
  const all = get(availableDiscriminatorsAtom);
  return all.find((d) => d.kind === "phase" && d.value === phase) ?? null;
});
